import { Suspense } from 'react';
import {
  createBrowserRouter, createRoutesFromElements, RouterProvider,
  Route, Navigate, Outlet, ScrollRestoration, useLocation,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import * as Sentry from '@sentry/react';
import theme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/useAuth';
import { UserSettingsProvider, useUserSettings } from './contexts/UserSettingsContext';
import { ErrorToastProvider } from './components/ErrorToastProvider';
import { FeedbackProvider } from './components/FeedbackProvider';
import { InviteProvider } from './components/InviteProvider';
import { StaleBundleProvider } from './components/StaleBundleProvider';
import { TaxonomyOverridesProvider } from './components/TaxonomyOverridesProvider';
import AppShell from './components/AppShell';
import NetworkStatusBanner from './components/NetworkStatusBanner';
import UpdateAvailableBar from './components/UpdateAvailableBar';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { lazyRoute } from './lib/lazyRoute';
import ErrorFallback from './components/ErrorFallback';
import RecoveryRedirect from './components/RecoveryRedirect';

// M28 — code splitting. Auth pages stay eager so unauthenticated users
// don't see a flash before login renders. Every other route is lazy:
// each becomes its own JS chunk, only fetched when first navigated to.
// The big wins are the multi-line editors (NewPurchasePage,
// EditPurchasePage, NewSalePage, EditSalePage) and the heavy detail
// pages (InventoryDetailPage, FirearmBuildDetailPage,
// RangeSessionDetailPage) — those weigh hundreds of KB each and most
// users won't visit them in a session.
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
// M29 — auth completeness: password-reset flow lands users on
// /forgot-password (request a link) and /reset-password (set a new
// password from the email link). Eager-loaded so users coming from an
// email link don't see a Suspense flash on a cold boot.
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

const DashboardPage         = lazyRoute('DashboardPage', () => import('./pages/dashboard/DashboardPage'));
const PurchasesPage         = lazyRoute('PurchasesPage', () => import('./pages/purchases/PurchasesPage'));
const PurchaseDetailPage    = lazyRoute('PurchaseDetailPage', () => import('./pages/purchases/PurchaseDetailPage'));
const NewPurchasePage       = lazyRoute('NewPurchasePage', () => import('./pages/purchases/NewPurchasePage'));
const EditPurchasePage      = lazyRoute('EditPurchasePage', () => import('./pages/purchases/EditPurchasePage'));
const WishlistPage          = lazyRoute('WishlistPage', () => import('./pages/purchases/WishlistPage'));
const SalesPage             = lazyRoute('SalesPage', () => import('./pages/sales/SalesPage'));
const SaleDetailPage        = lazyRoute('SaleDetailPage', () => import('./pages/sales/SaleDetailPage'));
const NewSalePage           = lazyRoute('NewSalePage', () => import('./pages/sales/NewSalePage'));
const EditSalePage          = lazyRoute('EditSalePage', () => import('./pages/sales/EditSalePage'));
const InventoryPage         = lazyRoute('InventoryPage', () => import('./pages/inventory/InventoryPage'));
const InventoryDetailPage   = lazyRoute('InventoryDetailPage', () => import('./pages/inventory/InventoryDetailPage'));
const InventoryEditorPage   = lazyRoute('InventoryEditorPage', () => import('./pages/inventory/InventoryEditorPage'));
const BuildsPage            = lazyRoute('BuildsPage', () => import('./pages/inventory/BuildsPage'));
const BuildCreatePage       = lazyRoute('BuildCreatePage', () => import('./pages/inventory/BuildCreatePage'));
const FirearmBuildDetailPage = lazyRoute('FirearmBuildDetailPage', () => import('./pages/inventory/FirearmBuildDetailPage'));
const BuildEditPage         = lazyRoute('BuildEditPage', () => import('./pages/inventory/BuildEditPage'));
const MagazineDetailPage    = lazyRoute('MagazineDetailPage', () => import('./pages/inventory/MagazineDetailPage'));
const MagazineEditorPage    = lazyRoute('MagazineEditorPage', () => import('./pages/inventory/MagazineEditorPage'));
const MembershipPage        = lazyRoute('MembershipPage', () => import('./pages/membership/MembershipPage'));
const MembershipDetailPage  = lazyRoute('MembershipDetailPage', () => import('./pages/membership/MembershipDetailPage'));
const MembershipEditorPage  = lazyRoute('MembershipEditorPage', () => import('./pages/membership/MembershipEditorPage'));
const PerkEditorPage        = lazyRoute('PerkEditorPage', () => import('./pages/membership/PerkEditorPage'));
const VenuesPage            = lazyRoute('VenuesPage', () => import('./pages/venues/VenuesPage'));
const VenueNewPage          = lazyRoute('VenueNewPage', () => import('./pages/venues/VenueNewPage'));
const VenueDetailPage       = lazyRoute('VenueDetailPage', () => import('./pages/venues/VenueDetailPage'));
const VenueEditPage         = lazyRoute('VenueEditPage', () => import('./pages/venues/VenueEditPage'));
const TripChecklistPage     = lazyRoute('TripChecklistPage', () => import('./pages/venues/TripChecklistPage'));
const RangeSessionsPage     = lazyRoute('RangeSessionsPage', () => import('./pages/range/RangeSessionsPage'));
const RangeSessionEditorPage = lazyRoute('RangeSessionEditorPage', () => import('./pages/range/RangeSessionEditorPage'));
const RangeSessionDetailPage = lazyRoute('RangeSessionDetailPage', () => import('./pages/range/RangeSessionDetailPage'));
const PackingKitsPage = lazyRoute('PackingKitsPage', () => import('./pages/range/PackingKitsPage'));
const PracticePlansPage     = lazyRoute('PracticePlansPage', () => import('./pages/range/PracticePlansPage'));
const DryFirePage           = lazyRoute('DryFirePage', () => import('./pages/training/DryFirePage'));
const GroupsPage            = lazyRoute('GroupsPage', () => import('./pages/groups/GroupsPage'));
const SettingsPage          = lazyRoute('SettingsPage', () => import('./pages/settings/SettingsPage'));
const RegulationsPage       = lazyRoute('RegulationsPage', () => import('./pages/regulations/RegulationsPage'));
const MyDocumentsPage       = lazyRoute('MyDocumentsPage', () => import('./pages/documents/MyDocumentsPage'));
const ChangelogPage         = lazyRoute('ChangelogPage', () => import('./pages/legal/ChangelogPage'));
// M33 — Legal pages. Public (no auth wrapper) so footer links in
// signup emails / external referrals work for logged-out visitors.
// Lazy-loaded; they're tiny but they pull `marked` + the raw
// markdown, and most users never visit them.
const PrivacyPage           = lazyRoute('PrivacyPage', () => import('./pages/legal/PrivacyPage'));
const TermsPage             = lazyRoute('TermsPage', () => import('./pages/legal/TermsPage'));
// M37 — admin surface. Lazy like every other route: only an admin ever
// loads these chunks, so they stay out of the default bundle entirely.
const AdminLayout           = lazyRoute('AdminLayout', () => import('./pages/admin/AdminLayout'));
const AdminHomePage         = lazyRoute('AdminHomePage', () => import('./pages/admin/AdminHomePage'));
const AdminFeedbackPage     = lazyRoute('AdminFeedbackPage', () => import('./pages/admin/AdminFeedbackPage'));
const AdminFeedbackDetailPage = lazyRoute('AdminFeedbackDetailPage', () => import('./pages/admin/AdminFeedbackDetailPage'));
const AdminInvitesPage      = lazyRoute('AdminInvitesPage', () => import('./pages/admin/AdminInvitesPage'));
const AdminActivityPage     = lazyRoute('AdminActivityPage', () => import('./pages/admin/AdminActivityPage'));
const AdminAuditPage        = lazyRoute('AdminAuditPage', () => import('./pages/admin/AdminAuditPage'));
const AdminStoragePage      = lazyRoute('AdminStoragePage', () => import('./pages/admin/AdminStoragePage'));
const AdminAnnouncementsPage = lazyRoute('AdminAnnouncementsPage', () => import('./pages/admin/AdminAnnouncementsPage'));
const AdminChangelogPage    = lazyRoute('AdminChangelogPage', () => import('./pages/admin/AdminChangelogPage'));
const AdminUpcConfigPage    = lazyRoute('AdminUpcConfigPage', () => import('./pages/admin/AdminUpcConfigPage'));
const AdminTagsPage         = lazyRoute('AdminTagsPage', () => import('./pages/admin/AdminTagsPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// M36 — `/` resolves to whichever module the user picked as their
// default landing page (Dashboard by default). Waits for the user
// settings fetch to complete so users with a non-default preference
// don't briefly land on Dashboard and then jump.
function LandingRedirect() {
  const { landingPage, loading } = useUserSettings();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  const map: Record<typeof landingPage, string> = {
    dashboard: '/dashboard',
    range: '/range',
    inventory: '/inventory',
    purchases: '/purchases',
  };
  return <Navigate to={map[landingPage]} replace />;
}

/**
 * M68d1 — /competitions is retired as a page; the Match standings live on the
 * range page as a tab. Redirect rather than 404, and MERGE the incoming query
 * string instead of replacing it: a bookmarked `/competitions?compose=match`
 * has to keep opening the create dialog, and dropping the params would land
 * users on a page that quietly ignored what they asked for.
 */
function CompetitionsRedirect() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('view', 'standings');
  return <Navigate to={{ pathname: '/range', search: `?${params.toString()}` }} replace />;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// M37 — admin gate. Sits inside ProtectedRoute (so `user` is already
// present); checks the is_admin flag on the loaded profile. Non-admins
// are bounced to the dashboard. Server-side RLS is the real enforcement
// — this just keeps the admin UI from rendering for the wrong people.
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!profile?.is_admin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

/**
 * M74b — the root layout route.
 *
 * Under the data router, `RouterProvider` takes no children, so everything
 * that used to sit between `<BrowserRouter>` and `<AppRoutes />` lives here
 * instead and renders `<Outlet />`. The nesting order is unchanged and load-
 * bearing: `ProtectedRoute` / `PublicRoute` / `AdminRoute` call `useAuth()`
 * and `LandingRedirect` calls `useUserSettings()`, so those providers MUST
 * stay above the route tree, not beside it.
 *
 * `RecoveryRedirect` calls `useNavigate()`, which is only legal inside the
 * router — hence a layout route rather than a wrapper around RouterProvider.
 */
function RootLayout() {
  return (
    <>
      {/* Safety net: routes password-recovery sessions to /reset-password no
          matter where Supabase's email link drops them. Sits at the top of the
          route tree so it runs on any landing path. */}
      <RecoveryRedirect />
      <AuthProvider>
        <UserSettingsProvider>
          {/* M45b — InviteProvider mounts inside AuthProvider so the
              Invite-friend modal's data fetches (which require auth)
              always have a session. The header AppBar icon + sidebar
              entry inside AppShell call useInvite().open(); both
              surfaces share one mounted modal. */}
          <InviteProvider>
            {/* M46 — Stale-bundle provider. Sits inside the auth +
                user-settings providers so the latest-changelog fetch
                has a session. AppShell consumes it for the dot on the
                "What's new" nav item; /changelog consumes it for the
                inline "Update available" card. One fetch, two surfaces. */}
            <StaleBundleProvider>
              {/* M69e — loads admin label/color overrides into the tag
                  resolver at boot + on focus. Non-blocking; AppShell reads
                  its version so a change re-renders the route tree. */}
              <TaxonomyOverridesProvider>
                <ErrorToastProvider>
                  {/* M30 — offline indicator. Sits above the route tree so
                      it persists across navigations. Collapsed when online. */}
                  <NetworkStatusBanner />
                  {/* A deploy renames every chunk, so a tab open across one is
                      a tab whose next lazy route 404s. `lazyRoute` recovers
                      from that on its own; this is the courtesy that makes the
                      reload a choice instead of a surprise. */}
                  <UpdateAvailableBar />
                  {/* M74d — restore list scroll position on back/forward, so a
                      round-trip into an editor page returns you where you were
                      rather than at the top. Data-router-only, which is why the
                      M74b migration had to land first. */}
                  <ScrollRestoration />
                  <Outlet />
                </ErrorToastProvider>
              </TaxonomyOverridesProvider>
            </StaleBundleProvider>
          </InviteProvider>
        </UserSettingsProvider>
      </AuthProvider>
    </>
  );
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />} errorElement={<RouteErrorBoundary />}>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      {/* /reset-password is intentionally NOT wrapped in PublicRoute.
          During the recovery flow Supabase mints a session for the user,
          so PublicRoute would bounce them away. The page itself handles
          the recovery state. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* M33 — Legal routes are wide-open (no Public/Protected wrap)
          so signed-in users and visitors both reach them. Suspense
          fallback lives in AppShell for protected routes; these
          render their own lightweight chrome, so wrap a thin
          fallback here too. */}
      <Route
        path="/privacy"
        element={(
          <Suspense fallback={<Box sx={{ p: 4 }}><LinearProgress /></Box>}>
            <PrivacyPage />
          </Suspense>
        )}
      />
      <Route
        path="/terms"
        element={(
          <Suspense fallback={<Box sx={{ p: 4 }}><LinearProgress /></Box>}>
            <TermsPage />
          </Suspense>
        )}
      />
      <Route
        path="/"
        element={(
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        )}
      >
        <Route index element={<LandingRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="purchases" element={<PurchasesPage />} />
        <Route path="purchases/new" element={<NewPurchasePage />} />
        <Route path="purchases/wishlist" element={<WishlistPage />} />
        <Route path="purchases/wishlist/:wishlistId" element={<WishlistPage />} />
        <Route path="purchases/:id" element={<PurchaseDetailPage />} />
        <Route path="purchases/:id/edit" element={<EditPurchasePage />} />
        <Route path="sales" element={<SalesPage />} />
        <Route path="sales/new" element={<NewSalePage />} />
        <Route path="sales/:id" element={<SaleDetailPage />} />
        <Route path="sales/:id/edit" element={<EditSalePage />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/builds" element={<BuildsPage />} />
        <Route path="inventory/builds/new" element={<BuildCreatePage />} />
        <Route path="inventory/builds/:buildId/edit" element={<BuildEditPage />} />
        <Route path="inventory/builds/:buildId" element={<FirearmBuildDetailPage />} />
        {/* M94b2 — the magazine's own routes. React Router RANKS routes by
            specificity, so a static segment beats a dynamic one no matter what
            order they are declared in: `inventory/magazines/:slotId` wins over
            `inventory/:type/:id` even though the latter is declared first in
            the file. Verified with `matchRoutes` against the installed router
            during the eng review, and pinned by a unit probe that declares them
            in the losing order and still resolves this one. `magazines` is not
            in `INVENTORY_TYPE_SLUGS`, so nothing else can mint the path. */}
        <Route path="inventory/magazines/:slotId/edit" element={<MagazineEditorPage />} />
        <Route path="inventory/magazines/:slotId" element={<MagazineDetailPage />} />
        {/* M74e — editor routes come before the detail route. This ordering is
            NOT what makes it work — ranking is, same as above — but it reads
            in the order a person expects, so it stays. Originally written as
            "order is load-bearing", which was wrong. */}
        <Route path="inventory/:type/new" element={<InventoryEditorPage />} />
        <Route path="inventory/:type/:id/edit" element={<InventoryEditorPage />} />
        <Route path="inventory/:type/:id" element={<InventoryDetailPage />} />
        <Route path="membership" element={<MembershipPage />} />
        <Route path="membership/new" element={<MembershipEditorPage />} />
        <Route path="membership/:id" element={<MembershipDetailPage />} />
        <Route path="membership/:id/edit" element={<MembershipEditorPage />} />
        <Route path="membership/:id/perks/new" element={<PerkEditorPage />} />
        <Route path="membership/:id/perks/:perkId/edit" element={<PerkEditorPage />} />
        <Route path="venues" element={<VenuesPage />} />
        <Route path="venues/new" element={<VenueNewPage />} />
        <Route path="venues/:id" element={<VenueDetailPage />} />
        <Route path="venues/:id/edit" element={<VenueEditPage />} />
        <Route path="venues/:id/trips/:tripId" element={<TripChecklistPage />} />
        <Route path="range" element={<RangeSessionsPage />} />
        <Route path="range/new" element={<RangeSessionEditorPage />} />
          <Route path="range/kits" element={<PackingKitsPage />} />
        <Route path="range/:id" element={<RangeSessionDetailPage />} />
        <Route path="range/:id/edit" element={<RangeSessionEditorPage />} />
        <Route path="dry-fire" element={<DryFirePage />} />
        <Route path="practice" element={<PracticePlansPage />} />
        {/* M68d1 — the Matches page folded into /range as the standings tab.
            The redirect PRESERVES the query string, so a bookmarked
            /competitions?compose=match still opens the create dialog, on the
            right tab. `replace` keeps the dead URL out of history. */}
        <Route path="competitions" element={<CompetitionsRedirect />} />
        <Route path="groups" element={<GroupsPage />} />
        <Route path="documents" element={<MyDocumentsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="regulations" element={<RegulationsPage />} />
        <Route path="changelog" element={<ChangelogPage />} />
        {/* M37 — admin surface. AdminRoute gates on profile.is_admin;
            AdminLayout renders the tab strip + its own Suspense. */}
        <Route path="admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route index element={<AdminHomePage />} />
          <Route path="feedback" element={<AdminFeedbackPage />} />
          <Route path="feedback/:id" element={<AdminFeedbackDetailPage />} />
          <Route path="invites" element={<AdminInvitesPage />} />
          <Route path="activity" element={<AdminActivityPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
          <Route path="storage" element={<AdminStoragePage />} />
          <Route path="announcements" element={<AdminAnnouncementsPage />} />
          <Route path="changelog" element={<AdminChangelogPage />} />
          <Route path="upc-lookup" element={<AdminUpcConfigPage />} />
          <Route path="tags" element={<AdminTagsPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  ),
);

export default function App() {
  return (
    <ThemeProvider theme={theme} defaultMode="system" noSsr>
      <CssBaseline enableColorScheme />
      {/*
        M32 — FeedbackProvider sits OUTSIDE the Sentry ErrorBoundary so
        the "Send feedback" button on the ErrorFallback can still call
        useFeedback() to open the dialog. The provider has no auth
        dependency — submitFeedback() reads the session straight from
        the supabase singleton, so it works whether or not AuthProvider
        is mounted underneath.

        M31 — Sentry ErrorBoundary catches anything that throws during
        render. Sentry init in main.tsx hooked it up to the SDK; here we
        just provide the fallback UI for the user. The boundary lives
        outside the router so a router-level crash still gets a usable
        fallback.

        M74b — the providers that used to live here moved into RootLayout,
        the router's pathless layout route: RouterProvider accepts no
        children. Everything above it is router-independent.
      */}
      <FeedbackProvider>
        <Sentry.ErrorBoundary
          fallback={({ resetError, error }) => (
            <ErrorFallback resetError={resetError} error={error} />
          )}
        >
          <RouterProvider router={router} />
        </Sentry.ErrorBoundary>
      </FeedbackProvider>
    </ThemeProvider>
  );
}
